import React, { forwardRef, useImperativeHandle, useEffect, useReducer, useRef, useState } from "react";
import { PageStates } from "../../../services/SystemNames";
import ShipApiRepository from "../../../repositories/api/ShipApiRepository";
import { DialogIconTitle, EditObservation } from "./PilotageDetailsShipDialogContent";
import Overlay from "../../../components/layout/overlay/Overlay";
import { isNullOrEmpty } from "../../../components/helpers/ObjectHelpers";
import { formatDateTimeForDisplay } from "../../../components/helpers/DateTimeHelpers";
import { useSuccessNotification, useErrorNotification } from 'components/hooks/useAppStore';

const Actions = {
    SetObservation: "setObservation",
    SetType: "setType",
    SetImage: "setImage",
    SetComment: "setComment",
    SetNewComment: "SetNewComment",
    SetNewType: "SetNewType",
    SetNewImage: "SetNewImage",
    ResetNewObservation: "ResetNewObservation",
    SetHttpStatusCode: "SetHttpStatusCode",
    SetCheckedMark: "SetCheckedMark",
    SetNewCheckedMark: "SetNewCheckedMark"
};

export const PilotageDetailsShipDialog = forwardRef(
    (
        {
            reloadObservations,
            fetchObservation,
            shipObservationTypes,
            shipId,
            isAllowedEdit,
            pilotageId,
        }, ref,
    ) => {

    PilotageDetailsShipDialog.displayName = "PilotageDetailsShipDialog";

    useImperativeHandle(ref, () => ({
        async onEditObservation(shipObservation) {
            setState(PageStates.Busy);

            const observation = await fetchObservation(shipObservation);
            handleShipObservationChanged(observation);

            isStateChanged.current = false;
            setState(PageStates.Edit);
        },
        onAddObservation() {
            setState(PageStates.Add)
        }
    }));

    const dialogScale = 0.95;

    const [observationData, dispatch] = useReducer(observationReducer, {});
    const [state, setState] = useState(PageStates.Default);
    const [width, setWidth] = useState(window.innerWidth * dialogScale);
    const [height, setHeight] = useState(window.innerHeight * dialogScale);

    const addSuccessNotification = useSuccessNotification();
    const addErrorNotification = useErrorNotification();

    const isStateChanged = useRef(false);
    useEffect(() => {
        window.screen.orientation.addEventListener('change', handleOrientationChange)
        return () => {
            window.screen.orientation.removeEventListener('change', handleOrientationChange);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function observationReducer(state, action) {
        switch (action.type) {
            case Actions.SetObservation: {
                return { 
                    ...state,
                    shipObservation: { 
                        ...action.newObservation,
                        sendToAuthorities: action.newObservation.sendToAuthorities ?? false
                    } 
                };
            }
            case Actions.SetType: {
                return {
                    ...state,
                    shipObservation: {
                        ...state.shipObservation,
                        shipObservationType: action.newObservationType,
                    },
                };
            }
            case Actions.SetNewType: {
                return {
                    ...state,
                    newShipObservation: {
                        ...state.newShipObservation,
                        shipObservationType: action.newObservationType,
                    },
                };
            }
            case Actions.SetComment: {
                return {
                    ...state,
                    shipObservation: {
                        ...state.shipObservation,
                        observation: action.newComment,
                    },
                };
            }
            case Actions.SetNewComment: {
                return {
                    ...state,
                    newShipObservation: {
                        ...state.newShipObservation,
                        observation: action.newComment,
                    },
                };
            }
            case Actions.SetImage: {
                return {
                    ...state,
                    shipObservation: {
                        ...state.shipObservation,
                        imageBase64Url: action.newImageUrl,
                        image: {
                            source: action.newImageUrl.split(",")[1],
                            imageHeight: action.newImageDimensions.width,
                            imageWidth: action.newImageDimensions.height,
                        },
                    },
                };
            }
            case Actions.SetNewImage: {
                return {
                    ...state,
                    newShipObservation: {
                        ...state.newShipObservation,
                        imageBase64Url: action.newImageUrl,
                        image: {
                            source: action.newImageUrl.split(",")[1],
                            imageHeight: action.newImageDimensions.width,
                            imageWidth: action.newImageDimensions.height,
                        },
                    },
                };
            }
            case Actions.ResetNewObservation: {
                return {
                    ...state,
                    newShipObservation: {
                    },
                };
            }
            case Actions.SetHttpStatusCode: {
                return {
                    ...state,
                    httpStatusCode: action.httpStatusCode,
                };
            }
            case Actions.SetNewCheckedMark: {
                return {
                    ...state,
                    newShipObservation: {
                        ...state.newShipObservation,
                        sendToAuthorities: action.newCheckedMarkState ?? false
                    }
                };
            }

            case Actions.SetCheckedMark: {
                return {
                    ...state,
                    shipObservation: {
                        ...state.shipObservation,
                        sendToAuthorities: action.newCheckedMarkState ?? false
                    }
                };
            }
            
            default: {
                console.error(`Unknow action: ${action.type}`);
                setState(PageStates.Error);
            }
        }
    }
    function handleShipObservationChanged(observation) {
        dispatch({
            type: Actions.SetObservation,
            newObservation: observation,
        });
        setState(PageStates.Default);
    };

    function handleImageSelected(imageUrl, imageDimensions) {
        if (state === PageStates.Edit) {
            dispatch({
                type: Actions.SetImage,
                newImageUrl: imageUrl,
                newImageDimensions: imageDimensions,
            });
            isStateChanged.current = true;
            setState(PageStates.Edit);
        }
        else if (state === PageStates.Add) {
            dispatch({
                type: Actions.SetNewImage,
                newImageUrl: imageUrl,
                newImageDimensions: imageDimensions,
            });
        }
    }

    const handleError = (response) => {
        if (response) {
            dispatch({
                type: Actions.SetHttpStatusCode,
                httpStatusCode: response,
            });
            setState(PageStates.Error);
        }
    };

    async function fetchAndStoreObservationAsync(shipObservation) {
        setState(PageStates.Busy);

        const observation = await fetchObservation(shipObservation);
        handleShipObservationChanged(observation);

        isStateChanged.current = false;
        setState(PageStates.Edit);
    }

    async function updateObservationAsync() {
        setState(PageStates.Busy);

        if (
            isNullOrEmpty(shipId) ||
            isNullOrEmpty(observationData.shipObservation.shipObservationType)
        ) {
            console.error("Missing data");
            setState(PageStates.Edit);
            return;
        }

        try {
            var shipObservationUpdate = {
                Observation: observationData.shipObservation.observation,
                ShipObservationType:
                    observationData.shipObservation.shipObservationType
                        .shipObservationTypeEnum,
                imageContent: observationData.shipObservation.image?.source ?? "",
                ETagValue: observationData.shipObservation.etagValue,
                SendToAuthorities: observationData.shipObservation.sendToAuthorities ?? false,
                PilotageId: pilotageId
            };
            var response = await ShipApiRepository.updateShipObservationAsync(
                observationData.shipObservation.shipObservationId,
                shipObservationUpdate,
            );
            if (response.ok) {
                addSuccessNotification("Observasjonen ble lagret");
                if (shipObservationUpdate.SendToAuthorities) {
                    await createShipPilotReportAsync(observationData.shipObservation.shipObservationId);
                }
            } else {
                addErrorNotification("Noe gikk galt ved lagring av observasjonen");
            }

            await fetchAndStoreObservationAsync(observationData.shipObservation);

            setState(PageStates.Default);
            reloadObservations();
        } catch (error) {
            handleError(response.status);
            console.error("An error occurred when saving observation", error);
        }
    }

    async function createObservationAsync() {
        setState(PageStates.Busy);

        if (
            isNullOrEmpty(shipId) ||
            isNullOrEmpty(
                observationData.newShipObservation.shipObservationType,
            )
        ) {
            console.error("Missing data");
            setState(PageStates.Add);
            return;
        }
        try {
            var shipObservationRequest = {
                ShipObservationType:
                    observationData.newShipObservation.shipObservationType
                        .shipObservationTypeEnum,
                ObservationComment: observationData.newShipObservation.observation,
                Image: observationData.newShipObservation.image ?? {
                    source: "",
                    imageHeight: 0,
                    imageWidth: 0,
                },
                SendToAuthorities: observationData.newShipObservation.sendToAuthorities,
                PilotageId: pilotageId
            };

            var response = await ShipApiRepository.createShipObservationAsync(
                shipId,
                shipObservationRequest,
            );

            if (response.ok === true) {
                addSuccessNotification("Observasjonen ble lagret");
                if (observationData.newShipObservation.sendToAuthorities) {
                    var observationId = await response.json();
                    await createShipPilotReportAsync(observationId);
                }
                resetNewObservationState();
            } else {
                addErrorNotification("Noe gikk galt ved lagring av observasjonen");
            }
        } catch (error) {
            handleError(response.status);
            console.error("An error occurred when saving observation", error);
        } finally {
            setState(PageStates.Default);
            reloadObservations();
        }
    }

    async function createShipPilotReportAsync(observationId) {
        setState(PageStates.Busy);
        var shipPilotReportRequest = {
            PilotageId: pilotageId,
            ShipId: shipId,
            ObservationComment: state === PageStates.Edit ? observationData.shipObservation.observation : observationData.newShipObservation.observation,
            ShipObservationId: observationId
        };

        var response = await ShipApiRepository.registerShipPilotReportAsync(shipPilotReportRequest);

        if(response.ok) {
            addSuccessNotification("Observasjonen ble sendt som rapport til SDIR");
        } else {
            addErrorNotification("Noe gikk galt ved innsending av rapport til SDIR");
        }
    }

    function handleObservationCommentUpdated(e) {
        if (state === PageStates.Edit) {
            dispatch({
                type: Actions.SetComment,
                newComment: e.target.value,
            });
        }
        else if (state === PageStates.Add) {
            dispatch({
                type: Actions.SetNewComment,
                newComment: e.target.value,
            });
        }
        isStateChanged.current = true;
    }

    const handleOrientationChange = () => {
        setWidth(window.innerWidth * dialogScale);
        setHeight(window.innerHeight * dialogScale);
        reloadObservations();
    };

    function handleObservationTypeSelected(newValue) {
        if (state === PageStates.Edit && !isNullOrEmpty(newValue)) {
            dispatch({
                type: Actions.SetType,
                newObservationType: newValue,
            });
            setState(PageStates.Edit);
        }
        else if (state === PageStates.Add) {
            dispatch({
                type: Actions.SetNewType,
                newObservationType: newValue,
            });
        }
        isStateChanged.current = true;
    }

    function resetNewObservationState() {
        dispatch({
            type: Actions.ResetNewObservation,
        });
    }

    function handleOnChecked(e) {
        if (isNullOrEmpty(e)) {
            dispatch({
                type: Actions.SetCheckedMark,
                newCheckedMarkState: e.value
            });
        }
        isStateChanged.current = true;

        if (state === PageStates.Edit && !isNullOrEmpty(e)) {
            dispatch({
                type: Actions.SetCheckedMark,
                newCheckedMarkState: e.value
            });
            setState(PageStates.Edit);
        }
        else if (state === PageStates.Add) {
            dispatch({
                type: Actions.SetNewCheckedMark,
                newCheckedMarkState: e.value
            });
        }
        isStateChanged.current = true;
    }

    const closeAndCleanupState = () => {
        if (resetNewObservationState) resetNewObservationState();
        setState(PageStates.Default);
        isStateChanged.current = false;
        setState(PageStates.Default);
    };

    return (
        <>
            {
                (() => {
                    switch (state) {
                        case PageStates.Edit:
                            return (
                                <EditObservation
                                    setState={setState}
                                    isAllowedEdit={isAllowedEdit}
                                    isStateChanged={isStateChanged}
                                    onTypeChanged={handleObservationTypeSelected}
                                    shipObservationTypes={shipObservationTypes}
                                    onSelectImage={handleImageSelected}
                                    shipObservation={observationData.shipObservation}
                                    onSaveButtonClicked={updateObservationAsync}
                                    onObservationCommentUpdated={handleObservationCommentUpdated}
                                    resetNewObservationState={resetNewObservationState}
                                    observationDate={formatDateTimeForDisplay(
                                        observationData.shipObservation.modifiedByPerson.modifiedDate,
                                    )}
                                    closeAndCleanupState={closeAndCleanupState}
                                    width={width}
                                    height={height}
                                    title={
                                        <DialogIconTitle
                                            title={"Rediger observasjon"}
                                        />
                                    }
                                    handleOnChecked={handleOnChecked}
                                />
                            );
                        case PageStates.Add:
                            return (
                                <EditObservation
                                    setState={setState}
                                    isAllowedEdit={isAllowedEdit}
                                    isStateChanged={isStateChanged}
                                    onTypeChanged={handleObservationTypeSelected}
                                    shipObservationTypes={shipObservationTypes}
                                    onSelectImage={handleImageSelected}
                                    shipObservation={observationData.newShipObservation}
                                    onSaveButtonClicked={createObservationAsync}
                                    onObservationCommentUpdated={handleObservationCommentUpdated}
                                    resetNewObservationState={resetNewObservationState}
                                    title={"Opprett ny observasjon"}
                                    closeAndCleanupState={closeAndCleanupState}
                                    width={width}
                                    height={height}
                                    handleOnChecked={handleOnChecked}
                                />
                            );
                        default: return null;
                    }
                })()
            }
            <Overlay isBusy={state === PageStates.Busy} />
        </>
    );
})
